(() => {
  'use strict';
  // The rack: hovering brings a piece forward and swings it a little with the mouse, the arrow keys move
  // between pieces.
  // Opening a piece is handled by ditto-shop.js (every [data-product-link]).
  function start() {
  const grids = [...document.querySelectorAll('.rack-grid, .shelf-grid')];
  if (!grids.length) return;
  const pieces = grids.flatMap(grid => [...grid.querySelectorAll('.piece')]);
  // Reveal: wait for the pictures (at most 2.5 s, so a slow picture never holds the rail hostage),
  // then let hangers and garments appear together, the shelf a beat after the rail.
  const allPictures = pieces.flatMap(piece => [...piece.querySelectorAll('.garment-visual img')]);
  const pictures = allPictures.filter(image => image.loading !== 'lazy');
  // Trim: a cutout often comes with transparent air around the garment (Finder's Remove Background keeps
  // the whole photo's canvas), which would leave the piece small and far below its hanger. So the garment's
  // real edges are measured once (a small canvas, alpha only; the CDN allows it) and the picture is sized
  // and placed so those edges meet the box, exactly as if the file had been cropped tight.
  const bounds = (source, w, h) => {
    const canvas = document.createElement('canvas'); canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(source, 0, 0, w, h);
    const data = ctx.getImageData(0, 0, w, h).data;
    let x0 = w, y0 = h, x1 = -1, y1 = -1;
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      if (data[(y * w + x) * 4 + 3] > 24) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
    }
    if (x1 < 0) return null;
    return { bx: (x0 / w).toFixed(4), by: (y0 / h).toFixed(4), bw: ((x1 - x0 + 1) / w).toFixed(4), bh: ((y1 - y0 + 1) / h).toFixed(4) };
  };
  const apply = (image, edges) => {
    if (!edges) return;
    image.dataset.bx = edges.bx; image.dataset.by = edges.by; image.dataset.bw = edges.bw; image.dataset.bh = edges.bh;
    fit(image);
  };
  const trim = image => {
    if (image.dataset.bw) return;
    // Not loaded yet (a cached picture can report complete before it has a size): measure when it is.
    if (!image.naturalWidth) {
      if (image.dataset.waiting) return;
      image.dataset.waiting = '1';
      image.addEventListener('load', () => { delete image.dataset.waiting; trim(image); }, { once: true });
      return;
    }
    const w = 160, h = Math.max(1, Math.round(image.naturalHeight / image.naturalWidth * w));
    try {
      apply(image, bounds(image, w, h));
    } catch (error) {
      // The canvas is tainted: the browser reused a copy of the picture fetched without CORS (a cache
      // from an earlier visit). Fetch it once more, with CORS and past the cache, and measure that copy.
      if (image.dataset.refetched) return;
      image.dataset.refetched = '1';
      fetch(image.currentSrc || image.src, { mode: 'cors', cache: 'reload' })
        .then(response => response.blob()).then(blob => createImageBitmap(blob))
        .then(bitmap => { try { apply(image, bounds(bitmap, w, h)); } finally { bitmap.close(); } })
        .catch(() => { /* a picture the browser will not let us read keeps its own framing */ });
    }
  };
  const fit = image => {
    if (!image.dataset.bw) return;
    const box = image.parentElement.getBoundingClientRect();
    if (!box.width || !box.height) return;
    const bx = +image.dataset.bx, by = +image.dataset.by, bw = +image.dataset.bw, bh = +image.dataset.bh;
    const scale = Math.min(box.width / (bw * image.naturalWidth), box.height / (bh * image.naturalHeight));
    const width = image.naturalWidth * scale, height = image.naturalHeight * scale;
    const onShelf = image.closest('.piece').dataset.hanger === 'shelf';
    const left = (box.width - bw * width) / 2 - bx * width;
    const top = onShelf ? box.height - (by + bh) * height : -by * height;
    image.style.cssText = `position:absolute;left:${left.toFixed(1)}px;top:${top.toFixed(1)}px;width:${width.toFixed(1)}px;height:${height.toFixed(1)}px;max-width:none;object-fit:fill`;
    // The SOLD OUT sign sits on the garment's own middle, not the box's: measured from the top of the
    // moving part (hanger + garment), which is what the sign is positioned in.
    const mid = image.parentElement.offsetTop + (onShelf ? box.height - bh * height / 2 : bh * height / 2);
    image.closest('.piece').style.setProperty('--sold-mid', `${mid.toFixed(1)}px`);
  };
  const refit = new ResizeObserver(entries => entries.forEach(entry => entry.target.querySelectorAll('img').forEach(fit)));
  allPictures.forEach(image => {
    refit.observe(image.parentElement);
    if (image.loading === 'lazy') { if (image.complete) trim(image); else image.addEventListener('load', () => trim(image), { once: true }); }
  });
  const decoded = Promise.all(pictures.map(image => image.decode().catch(() => null).then(() => trim(image))));
  // Safety net: whatever decode() and the load events did, every picture is measured once the page is in.
  window.addEventListener('load', () => allPictures.forEach(trim), { once: true });
  const patience = new Promise(resolve => setTimeout(resolve, 2500));
  Promise.race([decoded, patience]).then(() => grids.forEach(grid => {
    grid.classList.remove('is-loading');
    grid.classList.add('is-revealing');
    grid.addEventListener('animationend', () => grid.classList.remove('is-revealing'), { once: true, capture: true });
  }));
  const pointer = matchMedia('(hover: hover) and (pointer: fine)');
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const reset = piece => piece.style.removeProperty('--tilt');
  let active = -1;
  grids.forEach(grid => grid.classList.add('is-interactive'));
  function activate(index) {
    if (active === index) return;
    active = index;
    pieces.forEach((piece, i) => {
      piece.classList.toggle('is-active', i === index);
      if (i !== index) reset(piece);
    });
  }
  pieces.forEach((piece, index) => {
    piece.addEventListener('pointerenter', event => {
      if (pointer.matches && event.pointerType !== 'touch') activate(index);
    });
    piece.addEventListener('pointermove', event => {
      if (!pointer.matches || event.pointerType === 'touch') return;
      activate(index);
      if (reduced.matches) return;
      const bounds = piece.getBoundingClientRect();
      const tilt = Math.max(-1.3, Math.min(1.3, (event.clientX - bounds.left - bounds.width / 2) / bounds.width * 2.6));
      piece.style.setProperty('--tilt', `${tilt.toFixed(2)}deg`);
    });
    piece.addEventListener('pointerleave', () => { if (active === index) activate(-1); });
    piece.addEventListener('pointerdown', event => { if (event.pointerType === 'touch') activate(index); });
    piece.addEventListener('pointercancel', () => { if (active === index) activate(-1); });
    piece.addEventListener('focus', () => { if (piece.matches(':focus-visible')) activate(index); });
    piece.addEventListener('blur', () => { if (active === index) activate(-1); });
    piece.addEventListener('keydown', event => {
      const columns = Math.max(1, parseInt(getComputedStyle(piece.closest('.collection')).getPropertyValue('--columns'), 10) || 1);
      const offsets = {ArrowLeft:-1, ArrowRight:1, ArrowUp:-columns, ArrowDown:columns};
      let next;
      if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = pieces.length - 1;
      else if (event.key in offsets) next = Math.max(0, Math.min(pieces.length - 1, index + offsets[event.key]));
      else return;
      event.preventDefault();
      pieces[next].focus();
    });
  });
  reduced.addEventListener('change', () => pieces.forEach(reset));
  document.addEventListener('visibilitychange', () => { if (document.hidden) activate(-1); });
  }
  start();
  document.addEventListener('shopify:section:load', start);
})();
